import { observeMemberRestSessionExpiry } from '../session/session-expiry.js';
/**
 * TrpcCasesReader — the production `CasesReaderPort` adapter over the typed
 * tRPC client (034 US1, T016). Calls the live `member.cases.list` procedure:
 * the member's cookie session authenticates the same-origin request, and
 * `memberProcedure` on the API side guarantees only a member session reads it.
 *
 * Both `member` and `member.cases` are OPTIONAL on the published `AppRouter`
 * (the conditional-spread posture — `member.cases` is mounted only when the IS
 * client is configured). The adapter narrows before calling and fails loudly
 * (PortErrorBoundary catches it) rather than rendering a silent empty list —
 * an absent namespace is a composition/config error, not "no cases" (P7).
 */

import type { LanguageCode } from '../i18n/LanguagePort.js';
import { invalidateSession } from '../session/sessionInvalidation.js';
import type { WebTrpcClient } from '../trpc/client.js';
import type { CaseDetailView, CasesReaderPort, CasesView } from './CasesReaderPort.js';

export class TrpcCasesReader implements CasesReaderPort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async getCases(cursor?: string, language?: LanguageCode): Promise<CasesView> {
    const member = this.#client.member;
    if (member === undefined) {
      throw new Error(
        'TrpcCasesReader: the member namespace is absent on the AppRouter — ' +
          'the cases surface requires the 034 US1 API activation.',
      );
    }
    const cases = member.cases;
    if (cases === undefined) {
      throw new Error(
        'TrpcCasesReader: member.cases is absent on the AppRouter — the cases ' +
          'surface requires the IntegrationService client to be configured (034 US1).',
      );
    }
    // Cursor pagination (039 US1): thread the opaque cursor when fetching a
    // subsequent page. Language (039 US2) localizes each summary's category.
    return cases.list.query({
      ...(cursor !== undefined ? { cursor } : {}),
      ...(language !== undefined ? { language } : {}),
    });
  }

  async getCaseDetail(caseId: string, language?: LanguageCode): Promise<CaseDetailView> {
    const member = this.#client.member;
    if (member === undefined) {
      throw new Error(
        'TrpcCasesReader: the member namespace is absent on the AppRouter — ' +
          'the case-detail surface requires the 038 API activation.',
      );
    }
    const cases = member.cases;
    if (cases === undefined) {
      throw new Error(
        'TrpcCasesReader: member.cases is absent on the AppRouter — the case-detail ' +
          'surface requires the IntegrationService client to be configured (038).',
      );
    }
    // Language (039 US2) localizes the category label.
    return cases.detail.query({ caseId, ...(language !== undefined ? { language } : {}) });
  }

  async markConversationRead(
    caseId: string,
  ): Promise<'ok' | 'nothing-to-mark' | 'not-found' | 'unavailable'> {
    const member = this.#client.member;
    if (member === undefined) {
      throw new Error(
        'TrpcCasesReader: the member namespace is absent on the AppRouter — ' +
          'mark-conversation-read requires the 055 FR-004 API activation.',
      );
    }
    const cases = member.cases;
    if (cases === undefined) {
      throw new Error(
        'TrpcCasesReader: member.cases is absent on the AppRouter — mark-conversation-read ' +
          'requires the IntegrationService client to be configured (055 FR-004).',
      );
    }
    // 055 FR-004: ONE call marks the whole conversation read. The per-message
    // fan-out to the IS happens server-side (the IS exposes no conversation-level
    // read endpoint), so this is one round trip and one audit event per open.
    // The member id is session-derived on the API side.
    const result = await cases.markConversationRead.mutate({ caseId });
    // Fail closed: any status outside the known union becomes `unavailable`.
    switch (result.status) {
      case 'ok':
      case 'nothing-to-mark':
      case 'not-found':
        return result.status;
      default:
        return 'unavailable';
    }
  }

  async addMessage(caseId: string, text: string): Promise<'ok' | 'not-found' | 'unavailable'> {
    const member = this.#client.member;
    if (member === undefined) {
      throw new Error(
        'TrpcCasesReader: the member namespace is absent on the AppRouter — ' +
          'the case reply requires the 040 US1 API activation.',
      );
    }
    const cases = member.cases;
    if (cases === undefined) {
      throw new Error(
        'TrpcCasesReader: member.cases is absent on the AppRouter — the case reply ' +
          'requires the IntegrationService client to be configured (040 US1).',
      );
    }
    // 040 US1: add a member-authored reply. The member id is session-derived on
    // the API side; the text is trimmed + validated non-empty there too.
    const result = await cases.addMessage.mutate({ caseId, text });
    return result.status;
  }

  async uploadAttachment(
    caseId: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<'ok' | 'too-large' | 'unsupported' | 'not-found' | 'unavailable'> {
    // 040 US2: upload is a BINARY multipart POST, NOT a tRPC call (base64 over
    // JSON-RPC inflates ~33% and buffers the whole file). It goes to the same
    // member-guarded BFF route the download uses, same-origin so the cookie
    // session authenticates it. The server validates size + sniffed type
    // authoritatively; this maps its HTTP status to the honest outcome. On `ok`
    // the caller re-reads the detail (route-on-read) so the attachment shows.
    const form = new FormData();
    form.append('file', file, file.name);
    let res: Response;
    try {
      res = await fetch(`/member/cases/${encodeURIComponent(caseId)}/attachments`, {
        method: 'POST',
        credentials: 'same-origin',
        body: form,
        ...(signal !== undefined ? { signal } : {}),
      });
    } catch {
      // A network/transport failure OR a caller abort (051 timeout) is the
      // honest retryable unavailable state.
      return 'unavailable';
    }
    await observeMemberRestSessionExpiry(res);
    if (res.ok) {
      return 'ok';
    }
    if (res.status === 401) invalidateSession();
    switch (res.status) {
      case 413:
        return 'too-large';
      case 415:
        return 'unsupported';
      case 404:
        return 'not-found';
      default:
        // 503 and anything else → the honest temporarily-unavailable state.
        return 'unavailable';
    }
  }
}
